import { inject, Service } from '@angular/core';
import { CitadelWebService } from '@citadel/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { CountryModel } from '../models/country.model';
import { ComponentFSPDetailDTO } from '../models/component-fsp-detail.model';

@Service()
export class RegistrationService {
  private web = inject(CitadelWebService);
  private client = inject(HttpClient);

  // All of these are only assigned once validatePasswordStrength/calculatePasswordStrength has run.
  passwordStrength?: string;
  mediumPasswordScore: number = 10;
  strongPasswordScore: number = 20;
  bar0?: string;
  bar1?: string;
  bar2?: string;
  scoreColor?: string;
  hasUpperAndLower: boolean | null = null;
  hasMinLength: boolean | null = null;
  hasNumber: boolean | null = null;
  hasSymbol: boolean | null = null;
  // Only assigned (to `true`) when every check fails; never reset to `false`.
  switchBullet?: boolean;
  defaultBarColor: string = getComputedStyle(document.documentElement).getPropertyValue('--neutral-400');
  weakBarColor: string = getComputedStyle(document.documentElement).getPropertyValue('--palette-danger');
  mediumBarColor: string = getComputedStyle(document.documentElement).getPropertyValue('--palette-alternate-1');
  strongBarColor: string = getComputedStyle(document.documentElement).getPropertyValue('--status-success-default');

  validatePasswordStrength(password: string): void {
    const variations = {
      hasUpperAndLower: password.match(/^(?=.*[a-z])(?=.*[A-Z]).+$/),
      hasMinLength: password.length >= 8,
      hasNumber: password.match(/[0-9]+/),
      hasSymbol: password.match(/[$-/:-?{-~!&#*"^_\\@`\[\]]/g)
    };
    this.hasUpperAndLower = /^(?=.*[a-z])(?=.*[A-Z]).+$/.test(password);
    this.hasMinLength = password.length >= 8;
    this.hasNumber = /[0-9]+/.test(password);
    this.hasSymbol = /[$-/:-?{-~!&#*"^_\\@`\[\]]/g.test(password);
    if (!this.hasUpperAndLower && !this.hasMinLength && !this.hasNumber && !this.hasSymbol) {
      this.switchBullet = true;
    }
    let score = 0;
    let variationCount = 0;
    for (const check of Object.keys(variations) as (keyof typeof variations)[]) {
      variationCount += variations[check] ? 1 : 0;
    }
    score += (variationCount - 1) * 10;
    this.passwordStrength = this.calculatePasswordStrength(Math.trunc(score));
  }

  calculatePasswordStrength(score: number): string {
    if (score <= this.mediumPasswordScore) {
      this.setBarColors(1, this.weakBarColor);
      this.scoreColor = this.weakBarColor;
      return 'Weak';
    }
    if (score <= this.strongPasswordScore) {
      this.setBarColors(2, this.mediumBarColor);
      this.scoreColor = this.mediumBarColor;
      return 'Medium';
    }
    this.setBarColors(3, this.strongBarColor);
    this.scoreColor = this.strongBarColor;
    return 'Strong';
  }

  setBarColors(count: number, col: string) {
    this.bar0 = this.defaultBarColor;
    this.bar1 = this.defaultBarColor;
    this.bar2 = this.defaultBarColor;
    if (count >= 1) {
      this.bar0 = col;
    }
    if (count >= 2) {
      this.bar1 = col;
    }
    if (count >= 3) {
      this.bar2 = col;
    }
  }

  // Dead code: never called anywhere in the app. The outer `formGroup` parameter was unused - the
  // inner closure parameter of the same name shadowed it completely - so it's dropped here rather
  // than typed.
  ConfirmedValidator(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmedValidator
      ) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmedValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  // Left `any`: dead code (no caller anywhere in the app) and pre-existing bug - `colorScheme` is
  // declared but never assigned, so this always returns `undefined` despite its declared shape.
  // Not fixing the bug here; recorded, not resolved.
  getColorScheme(): any {
    const colorScheme: { background: 'light', sections: 'white', controls: 'light' } | undefined = undefined;
    return colorScheme;
  }
  getCountries(): Observable<CountryModel[]> {
    return this.client.get<CountryModel[]>('/api/Registration/countries');
  }

  // `country` (VerifyModel.Country) is the numeric country id, but every call site passes
  // `this.country.toString()` (registration-page.component.ts) - typed to match the actual call.
  identifyClient(id: string, emailAddress: string, cellNumber: string, countryCode: string, country: string, token: string): Observable<string> {
    return this.client.post<string>('/api/Registration/verify', { id, emailAddress, cellNumber, countryCode, country, token });
  }

  sendResetOtp(type: string, requestId: string): Observable<void> {
    return this.client.get<void>(`/api/Registration/sendresetotp/${type}/${requestId}`);
  }

  sendOtp(type: string, requestId: string): Observable<void> {

      return this.client.get<void>(`/api/Registration/sendotp/${type}/${requestId}`);

  }

  sendLoginOtp(type: string, clientUserName: string): Observable<void> {
    return this.client.get<void>(`/api/Registration/sendloginotp/${type}/${clientUserName}`);
  }

  saveNewUser(otp: string, password: string, clientId: string, appId: string): Observable<string> {
    return this.client.post<string>('/api/Registration/registeruser', {
      "otp": otp,
      "password": password,
      "clientId": clientId,
      "applicationId": appId
    });
  }

  verifyOTP(otp: string, clientId:string): Observable<boolean> {
    const result = this.client.get<boolean>(`/api/Registration/verifyotp/${otp}/${clientId}`);
    return result;
  }

  sendOtpEmail(requestId: string): Observable<void> {
    return this.client.post<void>('/api/auth/sendotpemail', { "requestID": requestId });
  }

  // POST /api/Communication/addVerification (SecurePortal.API CommunicationController), reached via
  // gateway path 'communication/addVerification' -> VerificationIntermediate. Not modelled here since
  // no field of the result is read by any caller; typed via the generic so a future caller gets it.
  addVerification(CellNumber: string, SmsMessage: string, CommunicationTypeL: number, ClientName: string): Observable<unknown> {
    const params: any = {};
    params.mobileNumber = CellNumber;
    params.smsMessage = SmsMessage;
    params.communicationType = CommunicationTypeL;
    params.clientName = ClientName;
    const result = this.web.post('communication/addVerification', params);
    return result;
  }

  // POST /api/Communication/verifyEMailAddress (SecurePortal.API CommunicationController) -> bool.
  verifyEmailAddress(email: string): Observable<boolean> {
    const params: any = { emailAddress: email };
    const result = this.web.post<boolean>('communication/verifyEMailAddress', params);
    return result;

  }

  getComponentFSPDetail(): Observable<ComponentFSPDetailDTO[]> {
    const result = this.web.get<ComponentFSPDetailDTO[]>('SCP.Client/GetComponentFSPDetail');
    return result;
  }
        
}
